import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { TemperMark } from "../components/Logo";
import { MaskedLines, FadeUp } from "../components/Reveal";
import Seo from "../components/Seo";
import { submitContact } from "../lib/api";
import BookCall from "../components/BookCall";

const INTERESTS = [
  "One-on-one coaching",
  "Team building clinic",
  "Organizational consulting",
  "The TEMPER Profile",
  "Something else",
];

const EMPTY = { name: "", email: "", organization: "", interest: INTERESTS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [phase, setPhase] = useState("form");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || phase === "saving") return;
    setPhase("saving");
    setError("");
    try {
      await submitContact(form);
      setPhase("sent");
    } catch {
      setPhase("form");
      setError("Something went wrong sending your message — please try again.");
    }
  };

  return (
    <>
      <Seo
        title="Contact TEMPER Leadership — Start the Conversation"
        description="Talk to Eric Ligeikis about leadership coaching, a team building clinic, or organizational consulting. Book a call or send a message — he reads every one."
        path="/contact"
      />
      <section data-testid="contact" className="pt-32 pb-24 md:pt-44">
      <div className="mx-auto grid max-w-[1440px] gap-16 px-6 md:grid-cols-12 md:px-10">
        <div className="md:col-span-5">
          <FadeUp>
            <p className="eyebrow flex items-center gap-3 text-ink/60">
              <TemperMark className="h-3 w-3 text-ember" /> Contact
            </p>
          </FadeUp>
          <MaskedLines
            as="h1"
            className="mt-8 font-display text-[clamp(2.8rem,5.5vw,5.5rem)] font-black leading-[0.98] tracking-[-0.02em]"
            lines={["Start the", <span key="x"><span className="text-ember">conversation.</span></span>]}
          />
          <FadeUp className="mt-8 max-w-md" delay={0.15}>
            <p className="leading-relaxed text-steel">
              Coaching, a clinic for your team, or counsel for the whole organization — tell
              Eric what's in front of you and he'll tell you honestly whether TEMPER is the
              right fit.
            </p>
          </FadeUp>
          <FadeUp className="mt-12" delay={0.2}>
            <p className="eyebrow text-ink/50">Email</p>
            <a
              href="mailto:eligeikis@gmail.com"
              data-testid="contact-email-link"
              className="mt-3 inline-block border-b border-ink/30 pb-1 font-mono text-sm transition-colors duration-300 hover:border-ember hover:text-ember"
            >
              eligeikis@gmail.com
            </a>
            <p className="mt-8 text-sm leading-relaxed text-steel">
              Eric reads every message himself.
            </p>
          </FadeUp>
        </div>

        <div className="md:col-span-7" data-testid="contact-panel">
          {phase === "sent" ? (
            <div className="flex h-full min-h-[420px] flex-col items-start justify-center border border-line p-10 md:p-14">
              <span className="flex h-14 w-14 items-center justify-center border border-ember text-ember" data-testid="contact-success-icon">
                <Check className="h-6 w-6" />
              </span>
              <h2 className="mt-8 font-display text-4xl font-extrabold tracking-tight" data-testid="contact-success-title">
                Message received.
              </h2>
              <p className="mt-4 max-w-md leading-relaxed text-steel">
                Thank you, {form.name.split(" ")[0]}. Your message was saved and Eric will
                reply from eligeikis@gmail.com. Want to talk sooner?{" "}
                <a href="#book-a-call" className="underline decoration-ember underline-offset-4 hover:text-ember">
                  Book a call directly
                </a>
                .
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => {
                  setForm(EMPTY);
                  setPhase("form");
                }}
                className="btn-outline mt-10"
              >
                Send another message
              </button>
            </div>
          ) : (
            <FadeUp delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="grid gap-8 md:grid-cols-2">
                <label className="block">
                  <span className="eyebrow text-ink/50">Name *</span>
                  <input
                    data-testid="contact-name"
                    className="field-input mt-2"
                    placeholder="Your name"
                    value={form.name}
                    onChange={set("name")}
                    required
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink/50">Work email *</span>
                  <input
                    data-testid="contact-email"
                    type="email"
                    className="field-input mt-2"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={set("email")}
                    required
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink/50">Organization</span>
                  <input
                    data-testid="contact-organization"
                    className="field-input mt-2"
                    placeholder="Company or team"
                    value={form.organization}
                    onChange={set("organization")}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink/50">What brings you here</span>
                  <select
                    data-testid="contact-interest"
                    className="field-input mt-2 cursor-pointer"
                    value={form.interest}
                    onChange={set("interest")}
                  >
                    {INTERESTS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="eyebrow text-ink/50">Message *</span>
                  <textarea
                    data-testid="contact-message"
                    className="field-input mt-2 min-h-[130px] resize-y"
                    placeholder="What's in front of you or your team right now?"
                    value={form.message}
                    onChange={set("message")}
                    required
                  />
                </label>
                {error && (
                  <p data-testid="contact-error" className="text-sm text-forge md:col-span-2">
                    {error}
                  </p>
                )}
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={phase === "saving"}
                    className="btn-ink disabled:opacity-60"
                  >
                    {phase === "saving" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send message"
                    )}
                  </button>
                </div>
              </form>
            </FadeUp>
          )}
        </div>
      </div>
      </section>
      <BookCall />
    </>
  );
}
